import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;
const EXPENSE_CATEGORIES = ['General', 'Rent', 'Utilities', 'Salaries', 'Supplies', 'Transportation', 'Marketing', 'Other'];

const INITIAL_STATE = {
  bill_number: '', amount: '', remarks: '',
  vendor_name: '', vendor_contact: '',
  category: 'General', payment_type: 'full', paid_amount: '',
};

export const ExpenseEntryForm = ({ selectedDate, onEntryAdded }) => {
  const [form, setForm] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bill_number || !form.amount) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      setLoading(true);
      const amount = parseFloat(form.amount);
      const paidAmount = form.payment_type === 'full' ? amount : parseFloat(form.paid_amount) || 0;
      const remaining = amount - paidAmount;
      await axios.post(`${API}/expenses`, {
        date: selectedDate,
        bill_number: form.bill_number,
        amount,
        remarks: form.remarks,
        vendor_name: form.vendor_name,
        vendor_contact: form.vendor_contact,
        category: form.category,
        payment_type: form.payment_type,
        paid_amount: paidAmount,
        remaining_balance: Math.max(remaining, 0),
        payment_status: remaining <= 0 ? 'paid' : 'pending',
      });
      toast.success('Expense added!');
      setForm(INITIAL_STATE);
      onEntryAdded();
    } catch (err) {
      toast.error('Failed to add expense');
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const remainingBalance = form.amount && form.paid_amount
    ? Math.max(parseFloat(form.amount) - parseFloat(form.paid_amount || 0), 0)
    : null;

  const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(v);

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="expense-entry-form">
      <div>
        <Label className="text-sm font-semibold">Bill Number *</Label>
        <Input value={form.bill_number} onChange={(e) => update('bill_number', e.target.value)} placeholder="BILL-001" required />
      </div>
      <div>
        <Label className="text-sm font-semibold">Category *</Label>
        <Select value={form.category} onValueChange={(v) => update('category', v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {EXPENSE_CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-sm font-semibold">Amount (₹) *</Label>
        <Input type="number" step="0.01" value={form.amount} onChange={(e) => update('amount', e.target.value)} className="currency" placeholder="0.00" required />
      </div>
      <div>
        <Label className="text-sm font-semibold">Vendor Name</Label>
        <Input value={form.vendor_name} onChange={(e) => update('vendor_name', e.target.value)} placeholder="Vendor/Supplier" />
      </div>
      <div>
        <Label className="text-sm font-semibold">Vendor Contact</Label>
        <Input value={form.vendor_contact} onChange={(e) => update('vendor_contact', e.target.value)} placeholder="Phone/Email" />
      </div>
      <div>
        <Label className="text-sm font-semibold">Remarks</Label>
        <Input value={form.remarks} onChange={(e) => update('remarks', e.target.value)} placeholder="Notes" />
      </div>
      <div>
        <Label className="text-sm font-semibold">Payment Type</Label>
        <Select value={form.payment_type} onValueChange={(v) => {
          if (v === 'full') update('payment_type', 'full');
          else setForm(prev => ({ ...prev, payment_type: v, paid_amount: '' }));
        }}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="full">Full Payment</SelectItem>
            <SelectItem value="partial">Partial Payment</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {form.payment_type === 'partial' && (
        <div className="bg-amber-50 border border-amber-200 p-3 rounded space-y-2">
          <div>
            <Label className="text-sm font-semibold text-amber-900">Paid Amount</Label>
            <Input type="number" step="0.01" value={form.paid_amount} onChange={(e) => update('paid_amount', e.target.value)} placeholder="Enter paid amount" className="currency" data-testid="expense-paid-amount" />
          </div>
          {remainingBalance !== null && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Remaining Balance:</span>
              <span className="font-bold text-amber-700">{formatCurrency(remainingBalance)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Status:</span>
            <span className={`font-semibold ${(parseFloat(form.amount || 0) - parseFloat(form.paid_amount || 0)) <= 0 ? 'text-green-600' : 'text-amber-600'}`}>
              {(parseFloat(form.amount || 0) - parseFloat(form.paid_amount || 0)) <= 0 ? 'Paid' : 'Pending Payment'}
            </span>
          </div>
        </div>
      )}
      <Button type="submit" disabled={loading} className="w-full premium-button bg-gradient-to-r from-red-500 to-pink-600 text-white hover:from-red-600 hover:to-pink-700" data-testid="submit-expense-btn">
        {loading ? 'Adding...' : 'Add Expense'}
      </Button>
    </form>
  );
};
